/* Agendamientos - listado de gestión (TD-11766).
 * Admins (AE_Agenda_Admin) ven todas y crean; gestores y responsables ven las suyas.
 */
import { IonBadge, IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import { CircularProgress } from "@material-ui/core";
import { addOutline, calendarOutline, hourglassOutline, peopleOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Header from "src/components/Header";
import AgendaAPI, { EstadoTema, TemaResumenGestion } from "src/services/agenda";
import { CATEGORIAS, colorCategoria, ESTADO_TEMA, ETIQUETA_MODALIDAD } from "src/utils/agenda";
import { mensajeError } from "src/utils/errores";
import "./Agenda.css";

type Filtro = "ACTIVAS" | EstadoTema;

const FILTROS: Array<{ valor: Filtro; etiqueta: string }> = [
  { valor: "ACTIVAS", etiqueta: "Activas" },
  { valor: "PUBLICADO", etiqueta: "Publicadas" },
  { valor: "BORRADOR", etiqueta: "Borradores" },
  { valor: "PAUSADO", etiqueta: "Pausadas" },
  { valor: "FINALIZADO", etiqueta: "Finalizadas" },
];

const AgendaGestion: React.FC = () => {
  const history = useHistory();
  const [present] = useIonToast();
  const [cargando, setCargando] = useState(true);
  const [lista, setLista] = useState<TemaResumenGestion[]>([]);
  const [esAdmin, setEsAdmin] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>("ACTIVAS");

  useEffect(() => {
    AgendaAPI.listarGestion()
      .then((r) => {
        setLista(r.datos);
        setEsAdmin(r.es_admin);
      })
      .catch((err) => {
        setLista([]);
        present({ message: mensajeError(err, "No se pudieron cargar las agendas"), duration: 3500, color: "danger" });
      })
      .finally(() => setCargando(false));
  }, [present]);

  const visibles = useMemo(
    () => lista.filter((t) => (filtro === "ACTIVAS" ? t.estado !== "FINALIZADO" : t.estado === filtro)),
    [lista, filtro]
  );

  return (
    <IonPage>
      <Header titulo="Gestión de agendas" />
      <IonContent id="agenda-gestion">
        <div className="agenda-page campos-mui">
          {esAdmin && (
            <IonButton expand="block" routerLink="/agenda/gestion/nueva">
              <IonIcon slot="start" icon={addOutline} /> Nueva agenda
            </IonButton>
          )}

          {cargando ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : lista.length === 0 ? (
            <div className="agenda-vacio">
              <IonIcon icon={calendarOutline} />
              {esAdmin
                ? "Todavía no hay agendas. Creá la primera: podés guardarla como borrador y publicarla cuando esté lista."
                : "No gestionás ni atendés ninguna agenda."}
            </div>
          ) : (
            <>
              <div className="agenda-chips" style={{ marginTop: 16 }}>
                {FILTROS.map((f) => (
                  <button
                    key={f.valor}
                    type="button"
                    className={`agenda-chip${filtro === f.valor ? " activo" : ""}`}
                    onClick={() => setFiltro(f.valor)}
                  >
                    {f.etiqueta}
                  </button>
                ))}
              </div>

              {visibles.length === 0 && <div className="agenda-vacio">No hay agendas en este filtro.</div>}

              {visibles.map((t) => (
                <div
                  key={t.id}
                  className="agenda-card acento"
                  role="button"
                  style={{ cursor: "pointer", ["--agenda-color" as any]: colorCategoria(t.categoria) }}
                  onClick={() => history.push(`/agenda/gestion/${t.id}`)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <div>
                      <span className="agenda-etiqueta">{CATEGORIAS[t.categoria]?.etiqueta}</span>
                      <p className="agenda-card-titulo">{t.titulo}</p>
                    </div>
                    <IonBadge color={ESTADO_TEMA[t.estado].color} style={{ alignSelf: "flex-start" }}>
                      {ESTADO_TEMA[t.estado].etiqueta}
                    </IonBadge>
                  </div>
                  <div className="agenda-meta">
                    <span>{t.tipo_agenda === "INDIVIDUAL" ? "Turnos individuales" : "Sesiones grupales"}</span>
                    <span>{ETIQUETA_MODALIDAD[t.modalidad]}</span>
                    <span>
                      <IonIcon icon={peopleOutline} /> {t.proximas} próxima(s)
                    </span>
                    {t.pendientes > 0 && (
                      <span style={{ color: "#b45309", fontWeight: 600 }}>
                        <IonIcon icon={hourglassOutline} /> {t.pendientes} por aprobar
                      </span>
                    )}
                    {t.espera > 0 && <span>{t.espera} en espera</span>}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AgendaGestion;
